import React, { FunctionComponent, ReactNode } from 'react'

import { ApolloError } from '@apollo/client'
import { TFunctionDetailedResult } from 'i18next'
import { SpaceProps } from 'styled-system'

import {
  ClientDebtorRelationshipStatus,
  ClientDebtorStatus,
  ContractStatus,
  FABClientStatus,
  SettlementStatus,
  TeamRole,
} from '@/models/back-office-pages/clients'
import { TaskAssignmentStatus, TaskInvoiceStatus } from '@/models/back-office-pages/debt-collection'
import { DUNSSearchType } from '@/models/back-office-pages/duns'
import { InvoiceExternalApprovalStatus, InvoiceStatus } from '@/models/back-office-pages/invoices'
import { LoanBookType } from '@/models/back-office-pages/loan-book'
import { AccountingNoteStatus } from '@/models/back-office-pages/notes'
import {
  ApprovalStatus,
  ClarificationCommentDTO,
  DebtorStatus,
  FundingLine,
  KycQuestionnaireStatus,
  KycRisk,
  ProspectStatus,
} from '@/models/back-office-pages/prospects'
import {
  BirthType,
  StoplistCompanyMatchType,
  StoplistPersonMatchType,
  StoplistStatus,
} from '@/models/back-office-pages/stop-list'
import { ClientStatus } from '@/models/back-office-pages/system'
import { Color } from '@/styles'

export enum LanguageList {
  en = 'en',
  lt = 'lt',
  lv = 'lv',
  nl = 'nl',
  pl = 'pl',
}

export enum AutoCommentLanguageList {
  en = 'en',
  lt = 'lt',
  nl = 'nl',
}

export enum InvestorLanguageList {
  en = 'en',
  nl = 'nl',
}

export enum DebtorStatementLanguageList {
  en = 'en',
  lt = 'lt',
}

export enum NLDebtorStatementDocumentLanguageList {
  en = 'en',
  nl = 'nl',
  de = 'de',
}

export enum NLDebtorStatementEmailLanguageList {
  en = 'en',
  nl = 'nl',
}

export enum SecurityMeasureLanguageList {
  en = 'en',
  nl = 'nl',
}

export enum PreferredLanguageList {
  en = 'en',
  lt = 'lt',
  lv = 'lv',
  nl = 'nl',
  pl = 'pl',
  fr = 'fr',
  it = 'it',
  es = 'es',
  sv = 'sv',
  no = 'no',
  da = 'da',
  de = 'de',
  nlBE = 'nlBE',
}

export enum YesNo {
  YES = 'YES',
  NO = 'NO',
}

export enum OverflowType {
  HIDDEN = 'hidden',
  UNSET = 'unset',
  AUTO = 'auto',
}

export enum DisplayType {
  PREVIEW = 'PREVIEW',
  FORM = 'FORM',
}

export enum ValueFormatType {
  DAYS = 'DAYS',
  MONTHS = 'MONTHS',
  CURRENCY = 'CURRENCY',
  PERCENTAGE = 'PERCENTAGE',
  DATE = 'DATE',
  NUMBER = 'NUMBER',
  TEXTAREA = 'TEXTAREA',
}

export enum AdornmentPosition {
  START = 'start',
  END = 'end',
}

export enum FormControlType {
  DISPLAY = 'DISPLAY',
  TEXT = 'TEXT',
  PUBLIC_TEXT = 'PUBLIC_TEXT',
  TEXTAREA = 'TEXTAREA',
  NUMERIC = 'NUMERIC',
  DAYS = 'DAYS',
  MONTHS = 'MONTHS',
  PHONE = 'PHONE',
  CURRENCY = 'CURRENCY',
  PERCENTAGE = 'PERCENTAGE',
  SELECT = 'SELECT',
  ASYNC_SELECT = 'ASYNC_SELECT',
  RADIO = 'RADIO',
  CHECKBOX = 'CHECKBOX',
  DROPZONE = 'DROPZONE',
  DATE_PICKER = 'DATE_PICKER',
  DATE_RANGE_PICKER = 'DATE_RANGE_PICKER',
  MULTI_SELECT = 'MULTI_SELECT',
  COMMENT = 'COMMENT',
  RICH_TEXT = 'RICH_TEXT',
  MULTI_CREATABLE = 'MULTI_CREATABLE',
  MONTH_PICKER = 'MONTH_PICKER',
  ARRAY = 'ARRAY',
  DUNS = 'DUNS',
  PATTERN = 'PATTERN',
}

export interface TabProps<T extends object> {
  key: string
  label: string
  content?: FunctionComponent
  contentProps?: T
  overflowType?: OverflowType
  error?: ApolloError
  hideTab?: boolean
}

export interface Map_String_DoubleScalar {
  [key: string]: number
}

export interface Map_String_LongScalar {
  [key: string]: number
}

export interface Field {
  name?: string
  label?: ReactNode
  value?: string | number | object | boolean | Date | Array<any>
  currency?: Currency
  width?: number | Array<number>
  control?: FormControlType
  options?: Array<SelectOption>
  hideField?: boolean
  valueFormat?: ValueFormatType
  previewValue?: ReactNode
  spaceProps?: SpaceProps
  fields?: Array<Field> | ((index?: number) => Array<Field>)
  addFieldArrayButtonText?: string
  removeFieldArrayButtonText?: string
  fieldArrayTitle?: string
  formatValue?: (val: string | number | object | boolean | Date) => string | number | object | boolean | Date
  decimalScale?: number
  maxLength?: number
  placeholder?: string
  checkboxLabel?: string
  checkboxFlag?: ReactNode
  color?: Color
  tooltip?: string
  maxDate?: Date | string
  minDate?: Date | string
  onChange?: (value: unknown) => void
  onBlur?: (value?: unknown) => void
  onFocus?: (value?: unknown) => void
  isSearchable?: boolean
  isClearable?: boolean
  isLoading?: boolean
  vertical?: boolean
  searchOnInputChange?: (searchText?: string) => void
  searchOnMenuOpen?: (option?: Option | SelectOption) => void
  onSelectChange?: (option?: Option | SelectOption) => void
  selectedOptionSearchValue?: string
  menuHeight?: number
  isDisabled?: boolean
  formatOptionLabel?: (option: unknown) => React.JSX.Element
  disabled?: boolean
  lastDay?: boolean
}

export interface FieldsSchema {
  sectionTitle?: string
  sectionTooltip?: string
  hideSection?: boolean
  fields: Array<Field>
  sectionDescription?: string
}

export interface Option {
  value: string | boolean | number | { [key: string]: string | number | boolean }
  label: string | React.JSX.Element
  isDisabled?: boolean

  [key: string]:
    | string
    | boolean
    | number
    | { [key: string]: string | number | boolean | object }
    | React.JSX.Element
    | Array<unknown>
    | object
}

export type SelectOption<TExtra extends Record<string, any> = Record<string, any>> = {
  value:
    | string
    | boolean
    | number
    | { [key: string]: string | number | boolean | object }
    | React.JSX.Element
    | Array<unknown>
    | object
    | TFunctionDetailedResult
  label: string | number | TFunctionDetailedResult | React.JSX.Element
} & TExtra

export interface MinMaxFilterInput {
  max: number
  min: number
}

export interface FromToFilterInput {
  from: string
  to: string
}

export enum SystemType {
  BACK_OFFICE = 'backOffice',
  CLIENT_PORTAL = 'clientPortal',
}

export enum NotesTableAssessor {
  COMPANY_NAME = 'companyName',
  NUMBER = 'noteNumber',
  CREDIT_NOTE_NUMBER = 'creditNoteNumber',
  ISSUE_DATE = 'issueDate',
  SOURCING_AGENT = 'sourcingAgentName',
  SOURCING_AGENT_ADMINISTRATES = 'sourcingAgentAdministrates',
  STATUS = 'status',
  TOTAL_ADVANCE_FACTOR_FEE = 'totalAdvanceFactorFeeNonDiscounting',
  TOTAL_ADVANCE_PAYABLE = 'totalInclVat',
  TOTAL_NOT_FACTORED_AMOUNT = 'totalAmountNotFactored',
  TOTAL_RESERVE_AMOUNT = 'totalAdvanceFactorFee',
  TOTAL_RESERVE_AMOUNT_VAT = 'totalReservedVat',
  TOTAL_FEES = 'totalFactorFee',
  TOTAL_FEES_VAT = 'totalFactorFeeVat',
  TOTAL = 'totalRefund',
  TOTAL_VAT = 'totalVat',
  SUBTOTAL_FEE = 'subtotalFee',
  ACTIONS = 'actions',
}

export enum AdministrationTypes {
  BE_LOCAL = 'BE local',
  BE_SPV = 'BE SPV',
  LT_LOCAL = 'LT local',
  LT_SPV = 'LT SPV',
  LV_LOCAL = 'LV local',
  LV_SPV = 'LV SPV',
  NL_LOCAL = 'NL local',
  NL_SPV = 'NL SPV',
  PL_LOCAL = 'PL local',
  PL_SPV = 'PL SPV',
}

export enum AdministrationLocalTypes {
  BE_LOCAL = 'BE local',
  LT_LOCAL = 'LT local',
  LV_LOCAL = 'LV local',
  NL_LOCAL = 'NL local',
  PL_LOCAL = 'PL local',
}

export enum AdministrationSPVTypes {
  BE_SPV = 'BE SPV',
  LT_SPV = 'LT SPV',
  LV_SPV = 'LV SPV',
  NL_SPV = 'NL SPV',
  PL_SPV = 'PL SPV',
}

export enum SourcingAgentTypes {
  BE_LOCAL = 'BE_LOCAL',
  BE_SPV = 'BE_SPV',
  LT_LOCAL = 'LT_LOCAL',
  LT_SPV = 'LT_SPV',
  LV_LOCAL = 'LV_LOCAL',
  LV_SPV = 'LV_SPV',
  NL_LOCAL = 'NL_LOCAL',
  NL_SPV = 'NL_SPV',
  PL_LOCAL = 'PL_LOCAL',
  PL_SPV = 'PL_SPV',
}

export enum ProspectAdministrationTypes {
  BE_LOCAL = 'BE local',
  LT_LOCAL = 'LT local',
  NL_LOCAL = 'NL local',
}

export enum UsersTableAssessor {
  NAME = 'firstName',
  EMAIL = 'email',
  COMPANY_NAME = 'companyName',
  POSITION = 'rolePosition',
  STATUS = 'status',
  CREATED_ON = 'createdOn',
  FIRST_LOGGED_IN = 'firstLoginAt',
  CAN_MANAGE_USERS = 'canCreateClientUser',
  ACTIONS = 'actions',
}

export type FileExtension =
  | '.pdf'
  | '.xls'
  | '.csv'
  | '.xlsx'
  | '.doc'
  | '.docx'
  | '.png'
  | '.jpeg'
  | '.jpg'
  | '.edoc'
  | '.adoc'
  | '.msg'
  | '.eml'
  | '.zip'
  | '.7z'
  | '.rar'
  | '.xml'

export type Status =
  | string
  | ProspectStatus
  | ApprovalStatus
  | YesNo
  | ClarificationStatus
  | DebtorStatus
  | KycQuestionnaireStatus
  | KycRisk
  | StoplistStatus
  | StoplistCompanyMatchType
  | StoplistPersonMatchType
  | ClientStatus
  | FABClientStatus
  | ContractStatus
  | SettlementStatus
  | TaskInvoiceStatus
  | TaskAssignmentStatus
  | ClientDebtorRelationshipStatus
  | ClientDebtorStatus
  | InvoiceStatus
  | InvoiceExternalApprovalStatus
  | AccountingNoteStatus

export interface RepresentativeDTO {
  addedBy: string
  birthYear: number | string
  birthDate: Date | string
  country: string
  createdOn: Date | string
  name: string
  personalId: string
  surname: string
  updatedBy: string
  updatedOn: Date | string
  birthType?: BirthType
  placeOfBirth?: string
}

export interface RepresentativeCompanyDTO {
  dunId: string
  address: string
  name: string
  registrationNumber: string
  country: string
}

export interface RepresentativeCompanyFormDTO {
  dunId: string
  searchType: DUNSSearchType
  companyCountryCode: string
  companyName: string
  [field: string]:
    | string
    | number
    | boolean
    | Date
    | Array<{
        [field: string]: string | number | boolean | Date | Array<{ [field: string]: string | number | boolean | Date }>
      }>
    | { [field: string]: string | number | boolean | Date | Array<{ [field: string]: string | number | boolean | Date }> }
}

export interface RepresentativesForm {
  representatives: Array<RepresentativeDTO>
}

export enum Currency {
  EUR = 'EUR',
  PLN = 'PLN',
}

export enum ReportStatus {
  CANCELED = 'CANCELED',
  ERROR = 'ERROR',
  PENDING = 'PENDING',
  READY = 'READY',
}

export interface ClarificationDTO {
  closed: boolean
  comment: string
  createdOn: string
  explanations: Array<ClarificationCommentDTO>
  fullName: string
  usersLogin: string
  id?: string
  questionToTeam?: TeamRole
}

export enum ClarificationStatus {
  OPEN = 'OPEN',
  ANSWERED = 'ANSWERED',
}

export interface ReportFileDTOInput {
  content: string
  fileName: string
  size: number
  id?: string

  __typename?: string
}

export enum CommentType {
  REQUIRED = 'REQUIRED',
  OPTIONAL = 'OPTIONAL',
  NO_COMMENT = 'NO_COMMENT',
}

export interface DownloadableFileDTO {
  content: string
  fileName: string
  size?: number
}

export enum RiskQuestionnaireType {
  PROSPECT_DEBTOR = 'PROSPECT_DEBTOR',
  LIMIT_INCREASE_DEBTOR = 'LIMIT_INCREASE_DEBTOR',
  DEBTOR = 'DEBTOR',
}

export enum ReportPeriod {
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  MONTHLY = 'MONTHLY',
  YEARLY = 'YEARLY',
  OTHER = 'OTHER',
}

export enum ClientReportPeriod {
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  MONTHLY = 'MONTHLY',
}

export enum ReportType {
  INVOICE = 'INVOICE',
  ACCOUNTING_NOTE = 'ACCOUNTING_NOTE',
  ACCOUNTING_RECONCILIATION = 'ACCOUNTING_RECONCILIATION',
  LOAN_BOOK_PAYOUT_NOTE = 'LOAN_BOOK_PAYOUT_NOTE',
  LOAN_BOOK_VAT_NOTE = 'LOAN_BOOK_VAT_NOTE',
}
export interface AutoReportDTO {
  createdOn: Date
  documentId: string
  fileName: string
  from: Date
  reportPeriod: ReportPeriod
  sourcingAgentAdministrates: AdministrationTypes
  to: Date
  reportType: ReportType
  status: ReportStatus
  uploader: string
  content: string
}

export interface Page_AutoReportDTO {
  content: AutoReportDTO[]
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export enum ScorePeriod {
  CURRENT = 'CURRENT',
  PREVIOUS = 'PREVIOUS',
}

export interface RiskScoresDTO {
  lastReportDate: Date
  ltScore?: number
  ltScoreChange?: number
  financialReportYear: string
  revenue: number
  revenueChange?: number
  netProfit: number
  netProfitChange?: number
  netProfitRate: number
  netProfitRateChange?: number
  equity: number
  equityChange?: number
  equityRate: number
  equityRateChange?: number
  currentRatio: number
  currentRatioChange?: number
  daysReceivable: number
  daysReceivableChange?: number
  daysPayable: number
  daysPayableChange?: number
  dayInventory: number
  dayInventoryChange?: number
  cashCycle: number
  cashCycleChange?: number
  probabilityOfDefault: number
  probabilityOfDefaultChange?: number
  creditLimit: number
  creditLimitChange?: number
  workingCapital: number
  workingCapitalChange?: number
  riskScore: number
  riskScoreChange?: number
  internationalScore: string
  spRating: string
  status: string
  dbt?: number
}

export interface LimitsDTO {
  fundingLine?: FundingLine
  productType?: LoanBookType
  limit: number
  usedLimit: number
  availableLimit: number
  currency: Currency
  limitExpiry: string
  [field: string]: string | number | boolean | Date | FundingLine | Currency | LoanBookType | undefined
}
